import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  PlusIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  BanknotesIcon,
  CalendarIcon,
  DocumentTextIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ArrowLeftIcon,
  UserIcon,
  ChatBubbleLeftRightIcon,
  CheckBadgeIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon as CheckCircleSolidIcon,
  XCircleIcon as XCircleSolidIcon,
  ClockIcon as ClockSolidIcon
} from '@heroicons/react/24/solid';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  useGetLoanRequestsQuery,
  useCancelLoanRequestMutation,
  useGetLoanRequestQuery
} from '../../services/loanApi';
import { Card, Button, Modal, DataTable, LoadingSpinner } from '../../components/common';
import LoanRequestForm from './LoanRequestForm';
import socketService from '../../services/socketService';
import toast from 'react-hot-toast';

const EmployeeLoanDashboard = () => {
  const { t } = useTranslation(['common', 'loans']);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  // Modal states
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRepaymentModal, setShowRepaymentModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  // API hooks
  const { data: loanRequestsData, isLoading, refetch } = useGetLoanRequestsQuery({
    employeeId: user.employeeId,
    page: currentPage,
    limit: pageSize
  });

  const { data: loanDetails } = useGetLoanRequestQuery(
    selectedLoan?._id || selectedLoan?.id,
    { skip: !selectedLoan || !showDetailsModal }
  );

  const [cancelLoan, { isLoading: cancelling }] = useCancelLoanRequestMutation();

  const handleCancelRequest = (loan) => {
    setSelectedLoan(loan);
    setShowCancelModal(true);
  };

  const confirmCancelRequest = async () => {
    if (!selectedLoan) return;

    try {
      await cancelLoan({ id: selectedLoan._id }).unwrap();

      toast.success(t('loans:requestCancelledSuccessfully'));
      setShowCancelModal(false);
      setSelectedLoan(null);
      refetch();
    } catch (error) {
      console.error('Error cancelling loan request:', error);
      toast.error(error.data?.message || t('loans:requestCancellationFailed'));
    }
  };

  const openDetailsModal = (loan) => {
    setSelectedLoan(loan);
    setShowDetailsModal(true);
  };

  const openRepaymentModal = (loan) => {
    setSelectedLoan(loan);
    setShowRepaymentModal(true);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircleSolidIcon className="h-5 w-5 text-green-500" />;
      case 'rejected':
        return <XCircleSolidIcon className="h-5 w-5 text-red-500" />;
      case 'active':
        return <ClockSolidIcon className="h-5 w-5 text-blue-500" />;
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-purple-500" />;
      case 'cancelled':
        return <XCircleSolidIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'active':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-purple-100 text-purple-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const columns = [
    {
      key: 'applicationNumber',
      header: 'Application #',
      render: (row) => (
        <div className="flex items-center space-x-2">
          <div className="flex-shrink-0">
            <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-md flex items-center justify-center">
              <DocumentTextIcon className="h-3 w-3 text-white" />
            </div>
          </div>
          <div>
            <div className="font-medium text-gray-900 text-xs">
              {row.applicationNumber}
            </div>
            <div className="text-xs text-gray-500">
              {row._id?.slice(-4).toUpperCase()}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'loanType',
      header: 'Type',
      render: (row) => {
        const getLoanTypeIcon = (type) => {
          switch (type) {
            case 'salary_advance':
              return <BanknotesIcon className="h-3 w-3 text-green-600" />;
            case 'personal_loan':
              return <UserIcon className="h-3 w-3 text-blue-600" />;
            case 'emergency_loan':
              return <ExclamationTriangleIcon className="h-3 w-3 text-red-600" />;
            default:
              return <DocumentTextIcon className="h-3 w-3 text-gray-600" />;
          }
        };

        const getLoanTypeColor = (type) => {
          switch (type) {
            case 'salary_advance':
              return 'bg-green-100 text-green-800 border-green-200';
            case 'personal_loan':
              return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'emergency_loan':
              return 'bg-red-100 text-red-800 border-red-200';
            default:
              return 'bg-gray-100 text-gray-800 border-gray-200';
          }
        };

        return (
          <div className="flex items-center space-x-1">
            {getLoanTypeIcon(row.loanType)}
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border ${getLoanTypeColor(row.loanType)}`}>
              {row.loanType?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A'}
            </span>
          </div>
        );
      }
    },
    {
      key: 'amount',
      header: 'Amount',
      render: (row) => (
        <div className="text-right">
          <div className="font-semibold text-gray-900 text-sm">
            ₹{row.amount?.toLocaleString('en-IN')}
          </div>
          <div className="text-xs text-gray-500">
            {row.repaymentType === 'emi_installments' ? 'EMI' : 'Full'}
          </div>
        </div>
      )
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <div className="flex items-center justify-center">
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(row.status)} shadow-sm`}>
            {getStatusIcon(row.status)}
            <span className="ml-1 capitalize">{row.status}</span>
          </span>
        </div>
      )
    },
    {
      key: 'appliedAt',
      header: 'Applied',
      render: (row) => (
        <div className="text-center">
          <div className="font-medium text-gray-900 text-xs">
            {new Date(row.appliedAt).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short'
            })}
          </div>
          <div className="text-xs text-gray-500">
            {new Date(row.appliedAt).toLocaleTimeString('en-GB', {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        </div>
      )
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <div className="flex items-center justify-center space-x-1">
          <button
            onClick={() => openDetailsModal(row)}
            className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm"
            title="View Details"
          >
            <EyeIcon className="h-3.5 w-3.5" />
          </button>

          {(row.status === 'approved' || row.status === 'active') && (
            <button
              onClick={() => openRepaymentModal(row)}
              className="inline-flex items-center px-2.5 py-1.5 border border-blue-300 rounded-lg text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 hover:border-blue-400 transition-all duration-200 shadow-sm"
              title="Repayment"
            >
              <BanknotesIcon className="h-3.5 w-3.5" />
            </button>
          )}

          {(row.status === 'submitted' || row.status === 'pending') && (
            <button
              onClick={() => handleCancelRequest(row)}
              className="inline-flex items-center px-2.5 py-1.5 border border-red-300 rounded-lg text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 hover:border-red-400 transition-all duration-200 shadow-sm"
              title="Cancel Request"
            >
              <XCircleIcon className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )
    }
  ];

  // Calculate stats
  const stats = loanRequestsData?.data?.reduce((acc, loan) => {
    acc.total++;
    switch (loan.status) {
      case 'approved':
        // Consider approved loans with payments as active
        if ((loan.totalPaid || 0) > 0) {
          acc.active++;
          acc.totalActiveAmount += loan.amount;
        } else {
          acc.approved++;
        }
        break;
      case 'active':
        acc.active++;
        acc.totalActiveAmount += loan.amount;
        break;
      case 'submitted':
      case 'pending':
        acc.pending++;
        break;
      case 'rejected':
        acc.rejected++;
        break;
      case 'completed':
        acc.completed++;
        break;
      case 'cancelled':
        acc.cancelled++;
        break;
      default:
        // Handle other statuses like 'draft', 'defaulted'
        break;
    }
    return acc;
  }, {
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    active: 0,
    completed: 0,
    cancelled: 0,
    totalActiveAmount: 0
  }) || {
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    active: 0,
    completed: 0,
    cancelled: 0,
    totalActiveAmount: 0
  };

  return (
    <>
      <Helmet>
        <title>{t('loans:myLoans')} - HRMS</title>
      </Helmet>

      {/* Modern Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-white/20 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center px-2 sm:px-4 py-2 text-gray-600 hover:text-gray-900 bg-white/50 rounded-lg sm:rounded-xl hover:bg-white/80 transition-all duration-200 group flex-shrink-0"
              >
                <ArrowLeftIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2 group-hover:-translate-x-1 transition-transform duration-200" />
                <span className="hidden xs:inline">{t('common:back')}</span>
              </button>
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent truncate flex items-center">
                  <BanknotesIcon className="h-6 w-6 sm:h-8 sm:w-8 mr-2 sm:mr-3 text-blue-600" />
                  {t('loans:myLoans')}
                </h1>
                <p className="text-xs sm:text-sm text-gray-600 mt-1 hidden sm:block">
                  {t('loans:manageYourLoanRequests')}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3 ml-4 flex-shrink-0">
              <Button
                onClick={() => setShowRequestForm(true)}
                className="px-3 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg sm:rounded-xl hover:from-blue-700 hover:to-indigo-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center text-sm"
              >
                <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">{t('loans:requestLoan')}</span>
                <span className="sm:hidden">Request</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4">

          {/* Compact Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
            <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-gray-200/60 p-3 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="p-1.5 bg-blue-100 rounded-md">
                    <DocumentTextIcon className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-600">
                      {t('loans:totalRequests')}
                    </p>
                    <p className="text-lg font-bold text-gray-900">
                      {stats.total}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-gray-200/60 p-3 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="p-1.5 bg-yellow-100 rounded-md">
                    <ClockIcon className="h-4 w-4 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-600">
                      {t('loans:pendingRequests')}
                    </p>
                    <p className="text-lg font-bold text-gray-900">
                      {stats.pending}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-gray-200/60 p-3 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="p-1.5 bg-green-100 rounded-md">
                    <BanknotesIcon className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-600">
                      {t('loans:activeLoans')}
                    </p>
                    <p className="text-lg font-bold text-gray-900">
                      {stats.active}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-gray-200/60 p-3 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="p-1.5 bg-purple-100 rounded-md">
                    <CheckCircleIcon className="h-4 w-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-600">
                      {t('loans:completedLoans')}
                    </p>
                    <p className="text-lg font-bold text-gray-900">
                      {stats.completed}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-gray-200/60 p-3 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="p-1.5 bg-red-100 rounded-md">
                    <XCircleIcon className="h-4 w-4 text-red-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-600">
                      Cancelled Loans
                    </p>
                    <p className="text-lg font-bold text-gray-900">
                      {stats.cancelled}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Compact Active Loan Amount */}
          {stats.totalActiveAmount > 0 && (
            <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-gray-200/60 p-3 shadow-sm mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-md">
                    <ArrowTrendingUpIcon className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-700">
                      {t('loans:totalActiveLoanAmount')}
                    </p>
                    <p className="text-xl font-bold text-blue-900">
                      ₹{stats.totalActiveAmount.toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-blue-600 font-medium">
                    {t('loans:activeLoanNote')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Modern Loan Requests Table */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg border border-gray-200/60 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200/60 bg-gradient-to-r from-gray-50/50 to-blue-50/30">
                <h3 className="text-base font-semibold text-gray-900 flex items-center">
                  <DocumentTextIcon className="h-4 w-4 mr-2 text-blue-600" />
                  Loan Applications ({loanRequestsData?.pagination?.total || 0})
                </h3>
              </div>
              <div className="overflow-x-auto">
              <DataTable
                columns={columns}
                data={loanRequestsData?.data || []}
                loading={isLoading}
                pagination={loanRequestsData?.pagination}
                onPageChange={setCurrentPage}
                emptyMessage={t('loans:noLoanRequests')}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Request Form Modal */}
      <Modal
        isOpen={showRequestForm}
        onClose={() => setShowRequestForm(false)}
        title={t('loans:requestLoan')}
        size="lg"
      >
        <LoanRequestForm onSuccess={() => setShowRequestForm(false)} />
      </Modal>

      {/* Details Modal */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title={t('loans:loanDetails')}
        size="lg"
      >
        {loanDetails?.data && Object.keys(loanDetails.data).length > 0 ? (
          <div className="space-y-6">
            {/* Header Card */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <DocumentTextIcon className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-blue-900">
                      {loanDetails.data.applicationNumber}
                    </h3>
                    <p className="text-sm text-blue-700">
                      {loanDetails.data.employeeId?.firstName} {loanDetails.data.employeeId?.lastName} • {loanDetails.data.employeeId?.employeeId}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(loanDetails.data.status)}`}>
                    {getStatusIcon(loanDetails.data.status)}
                    <span className="ml-2 capitalize">{loanDetails.data.status}</span>
                  </span>
                </div>
              </div>
            </div>

            {/* Loan Information Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <BanknotesIcon className="h-5 w-5 mr-2 text-blue-600" />
                  Loan Information
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-600">Loan Type</span>
                    <span className="text-sm font-semibold text-gray-900 capitalize">
                      {loanDetails.data.loanType?.replace('_', ' ') || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-600">Amount</span>
                    <span className="text-lg font-bold text-green-600">
                      ₹{loanDetails.data.amount?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-600">Applied Date</span>
                    <span className="text-sm text-gray-900">
                      {new Date(loanDetails.data.appliedAt).toLocaleDateString('en-GB')}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-sm font-medium text-gray-600">Repayment Type</span>
                    <span className="text-sm font-semibold text-gray-900 capitalize">
                      {loanDetails.data.repaymentType?.replace('_', ' ') || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Employee Information */}
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <UserIcon className="h-5 w-5 mr-2 text-blue-600" />
                  Employee Information
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-600">Name</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {loanDetails.data.employeeId?.firstName} {loanDetails.data.employeeId?.lastName}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-600">Employee ID</span>
                    <span className="text-sm text-gray-900">
                      {loanDetails.data.employeeId?.employeeId}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-600">Department</span>
                    <span className="text-sm text-gray-900">
                      {loanDetails.data.employeeId?.department?.name || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-sm font-medium text-gray-600">Designation</span>
                    <span className="text-sm text-gray-900">
                      {loanDetails.data.employeeId?.designation?.name || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Purpose */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2 text-blue-600" />
                Purpose
              </h4>
              <p className="text-gray-700 bg-gray-50 rounded-lg p-4">
                {loanDetails.data.purpose}
              </p>
            </div>

            {/* EMI Details */}
            {loanDetails.data.emiDetails && (
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <CalendarIcon className="h-5 w-5 mr-2 text-blue-600" />
                  EMI Details
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">
                      {loanDetails.data.emiDetails.numberOfInstallments}
                    </p>
                    <p className="text-sm text-gray-600">Installments</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">
                      ₹{loanDetails.data.amount?.toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-600">Loan Amount</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-600">
                      ₹{loanDetails.data.totalPaid?.toLocaleString() || 0}
                    </p>
                    <p className="text-sm text-gray-600">Total Paid</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-600">
                      ₹{loanDetails.data.remainingBalance?.toLocaleString() || loanDetails.data.amount?.toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-600">Remaining Balance</p>
                  </div>
                </div>
              </div>
            )}

            {/* Repayment Schedule */}
            {loanDetails.data.repaymentSchedule && loanDetails.data.repaymentSchedule.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <CalendarIcon className="h-5 w-5 mr-2 text-blue-600" />
                  Repayment Schedule
                </h4>
                <div className="space-y-3">
                  {loanDetails.data.repaymentSchedule.map((installment) => (
                    <div
                      key={installment.installmentNumber}
                      className={`p-4 rounded-lg border ${
                        installment.status === 'paid'
                          ? 'bg-green-50 border-green-200'
                          : installment.status === 'overdue'
                          ? 'bg-red-50 border-red-200'
                          : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-4">
                          <div className={`w-3 h-3 rounded-full ${
                            installment.status === 'paid'
                              ? 'bg-green-500'
                              : installment.status === 'overdue'
                              ? 'bg-red-500'
                              : 'bg-yellow-500'
                          }`}></div>
                          <div>
                            <p className="font-medium text-gray-900">
                              Installment {installment.installmentNumber}
                            </p>
                            <p className="text-sm text-gray-600">
                              Due: {new Date(installment.dueDate).toLocaleDateString('en-GB')}
                            </p>
                            {installment.paidAt && (
                              <p className="text-xs text-green-600">
                                Paid: {new Date(installment.paidAt).toLocaleDateString('en-GB')}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900">
                            ₹{installment.amount?.toLocaleString()}
                          </p>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            installment.status === 'paid'
                              ? 'bg-green-100 text-green-800'
                              : installment.status === 'overdue'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {installment.status === 'paid' && <CheckCircleIcon className="h-3 w-3 mr-1" />}
                            {installment.status === 'overdue' && <XCircleIcon className="h-3 w-3 mr-1" />}
                            {installment.status === 'pending' && <ClockIcon className="h-3 w-3 mr-1" />}
                            <span className="capitalize">{installment.status}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Decision Information */}
            {loanDetails.data.finalDecision && (
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <CheckBadgeIcon className="h-5 w-5 mr-2 text-green-600" />
                  Decision Information
                </h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600">Decided By</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {loanDetails.data.finalDecision.decidedBy?.firstName && loanDetails.data.finalDecision.decidedBy?.lastName
                        ? `${loanDetails.data.finalDecision.decidedBy.firstName} ${loanDetails.data.finalDecision.decidedBy.lastName}`
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600">Decision Date</span>
                    <span className="text-sm text-gray-900">
                      {new Date(loanDetails.data.finalDecision.decidedAt).toLocaleDateString('en-GB')}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-600 block mb-2">Remarks</span>
                    <p className="text-sm text-gray-900 bg-white rounded-md p-3 border">
                      {loanDetails.data.finalDecision.remarks}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end pt-4 border-t border-gray-200">
              <Button
                onClick={() => setShowDetailsModal(false)}
                className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 font-medium"
              >
                Close
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex justify-center items-center py-12">
            <div className="text-center">
              <LoadingSpinner />
              <p className="text-sm text-gray-600 mt-4">Loading loan details...</p>
            </div>
          </div>
        )}
      </Modal>

      {/* Repayment Modal */}
      <Modal
        isOpen={showRepaymentModal}
        onClose={() => setShowRepaymentModal(false)}
        title={t('loans:repaymentSchedule')}
        size="lg"
      >
        {selectedLoan && (
          <div className="space-y-6">
            {/* Loan Summary Card */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <BanknotesIcon className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-blue-900">
                    {selectedLoan.applicationNumber}
                  </h3>
                  <p className="text-sm text-blue-700">
                    {selectedLoan.loanType?.replace('_', ' ')} • ₹{selectedLoan.amount?.toLocaleString()}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    Status: <span className="capitalize font-medium">{selectedLoan.status}</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Repayment Schedule */}
            {selectedLoan.repaymentSchedule && selectedLoan.repaymentSchedule.length > 0 ? (
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <CalendarIcon className="h-5 w-5 mr-2 text-blue-600" />
                  Repayment Schedule
                </h4>
                <div className="space-y-3">
                  {selectedLoan.repaymentSchedule.map((installment) => (
                    <div
                      key={installment.installmentNumber}
                      className={`p-4 rounded-lg border ${
                        installment.status === 'paid'
                          ? 'bg-green-50 border-green-200'
                          : installment.status === 'overdue'
                          ? 'bg-red-50 border-red-200'
                          : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-4">
                          <div className={`w-3 h-3 rounded-full ${
                            installment.status === 'paid'
                              ? 'bg-green-500'
                              : installment.status === 'overdue'
                              ? 'bg-red-500'
                              : 'bg-yellow-500'
                          }`}></div>
                          <div>
                            <p className="font-medium text-gray-900">
                              Installment {installment.installmentNumber}
                            </p>
                            <p className="text-sm text-gray-600">
                              Due: {new Date(installment.dueDate).toLocaleDateString('en-GB')}
                            </p>
                            {installment.paidAt && (
                              <p className="text-xs text-green-600">
                                Paid: {new Date(installment.paidAt).toLocaleDateString('en-GB')}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900">
                            ₹{installment.amount?.toLocaleString()}
                          </p>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            installment.status === 'paid'
                              ? 'bg-green-100 text-green-800'
                              : installment.status === 'overdue'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {installment.status === 'paid' && <CheckCircleIcon className="h-3 w-3 mr-1" />}
                            {installment.status === 'overdue' && <XCircleIcon className="h-3 w-3 mr-1" />}
                            {installment.status === 'pending' && <ClockIcon className="h-3 w-3 mr-1" />}
                            <span className="capitalize">{installment.status}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Summary */}
                <div className="mt-6 bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-green-600">
                        {selectedLoan.repaymentSchedule.filter(inst => inst.status === 'paid').length}
                      </p>
                      <p className="text-sm text-gray-600">Paid</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-yellow-600">
                        {selectedLoan.repaymentSchedule.filter(inst => inst.status === 'pending').length}
                      </p>
                      <p className="text-sm text-gray-600">Pending</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-red-600">
                        {selectedLoan.repaymentSchedule.filter(inst => inst.status === 'overdue').length}
                      </p>
                      <p className="text-sm text-gray-600">Overdue</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center">
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-2" />
                  <p className="text-sm text-yellow-800">
                    No repayment schedule available for this loan.
                  </p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end pt-4 border-t border-gray-200">
              <Button
                onClick={() => setShowRepaymentModal(false)}
                className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 font-medium"
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Cancel Confirmation Modal */}
      <Modal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        title={t('loans:cancelLoanRequest')}
        size="md"
      >
        <div className="space-y-6">
          {/* Loan Summary Card */}
          <div className="bg-gradient-to-r from-red-50 to-rose-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <XCircleIcon className="h-8 w-8 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-red-900">
                  {selectedLoan?.applicationNumber}
                </h3>
                <p className="text-sm text-red-700">
                  {selectedLoan?.loanType?.replace('_', ' ')} • ₹{selectedLoan?.amount?.toLocaleString()}
                </p>
                <p className="text-xs text-red-600 mt-1">
                  Applied on {selectedLoan ? new Date(selectedLoan.appliedAt).toLocaleDateString('en-GB') : ''}
                </p>
              </div>
            </div>
          </div>

          {/* Cancellation Details */}
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-red-900 mb-2 flex items-center">
                <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                Cancellation Confirmation
              </h4>
              <p className="text-sm text-red-700">
                You are about to cancel this loan request. This action cannot be undone and the request will be permanently cancelled.
              </p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-yellow-900 mb-2">Important Note</h4>
              <p className="text-sm text-yellow-700">
                Only pending loan requests can be cancelled. Once a loan is approved, it cannot be cancelled through this system.
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button
              variant="secondary"
              onClick={() => setShowCancelModal(false)}
              className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-all duration-200 font-medium"
            >
              {t('common:keep')}
            </Button>
            <Button
              variant="danger"
              onClick={confirmCancelRequest}
              loading={cancelling}
              className="px-8 py-2.5 bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white rounded-xl transition-all duration-200 font-semibold shadow-lg hover:shadow-xl"
            >
              <XCircleIcon className="h-5 w-5 mr-2" />
              {t('common:cancel')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default EmployeeLoanDashboard;